import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import baseApi from '../../../api/baseApi';
import { ID, PATH, ERRORS, META } from '../../../constants';
import { setErrorStatus, getStatisticsUrlForSearch, getStatisticsImg, getSearchPageTitle } from '../../../utils';

import BoxWithUmbracoSearch from '../../UI/BoxWithUmbracoSearch/BoxWithumbracoSearch';
import ContactFooter from '../../UI/ContactFooter/ContactFooter';
import ListLinks from '../../UI/ListLinks/ListLinks';
import Loader from '../../UI/Loader/Loader';
import Search from '../../UI/Search/Search';
import PageUnavailable from '../Error/PageUnavailable/PageUnavailable';

const SearchResult = (props) => {
    const { t } = useTranslation();
    const [isMounted, setIsMounted] = useState(false);
    const [statisticsUrl, setStatisticsUrl] = useState('');
    const [searchParams] = useSearchParams();
    const [resultsToDisplay, setResultsToDisplay] = useState([]);
    const [searchPhrase, setSearchPhrase] = useState(searchParams.get('phrase') ?? '');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [initialPhrase] = useState(searchPhrase);
    const [hasThePhraseBeenUpdated, setHasTheTermBeenUpdated] = useState(false);
    const [isFetchingFromSearchResult, setIsFetchingFromSearchResult] = useState(true);
    const [triggerAutocompleteHiding, setTriggerAutocompleteHiding] = useState(Date.now());

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    useEffect(() => {
        if (hasThePhraseBeenUpdated) {
            return;
        }

        const isSearchPhraseEqualToInitialTerm = searchPhrase === initialPhrase;
        setIsFetchingFromSearchResult(isSearchPhraseEqualToInitialTerm);
        setHasTheTermBeenUpdated(!isSearchPhraseEqualToInitialTerm);
    }, [
        initialPhrase,
        searchPhrase,
        hasThePhraseBeenUpdated,
        setHasTheTermBeenUpdated
    ]);

    useEffect(() => {
        if (!isMounted) {
            return;
        }

        var currentSearchPhrase = searchParams.get('phrase');
        if (!currentSearchPhrase) {
            setIsLoading(false);
            return;
        }

        const handleStatistics = (searchPhrase, searchConfidenceFromSearch = 0 /* zero when nothing was found */) => {
            if (!searchPhrase) {
                return;
            }

            setStatisticsUrl(getStatisticsUrlForSearch(searchPhrase, searchConfidenceFromSearch));
        };

        const search = async () => {
            setIsLoading(true);
            await baseApi
                .post(
                    '/search',
                    { searchText: currentSearchPhrase },
                    {
                        params: {
                            limit: 10
                        }
                    }
                )
                .then((response) => {
                    if (!isMounted) {
                        return;
                    }
                    handleStatistics(currentSearchPhrase, response.data.metadata.searchConfidence);
                    return response.data.data;
                })
                .then(setResultsToDisplay)
                .then(() => setTriggerAutocompleteHiding(Date.now()))
                .catch(error => {
                    if (!error?.response) {
                        setError(ERRORS.unhandled);
                    } else {
                        setErrorStatus(setError, error);
                    }
                    setResultsToDisplay([]);
                    handleStatistics(currentSearchPhrase);
                })
                .finally(() => setIsLoading(false));
        };
        search();
    }, [searchParams, props.history, setTriggerAutocompleteHiding, isMounted]);

    if (!isMounted) {
        return null;
    }

    if (error === ERRORS.unhandled) {
        return <PageUnavailable />;
    }

    const getHeader = () => {
        if (isLoading) {
            return <Loader large spinner />;
        }

        const phrase = searchParams.get('phrase');
        if (!phrase) {
            return (
                <>
                    <h2
                        className="help-page__headline"
                        data-cy="search-results-header"
                    >
                        {t('layout.searchPage.invalidInput')}
                    </h2>
                    <p>{META.homePageDescription}</p>
                </>
            );
        }

        const resultsCount = resultsToDisplay?.length;
        const header = `${resultsCount ? resultsCount + ' ' + `${t('layout.searchPage.header.results')}` : `${t('layout.searchPage.header.empty')}`}
        ${t('layout.searchPage.header.line')} "${phrase}"`;
        const headerParagraph = resultsCount
            ? `${t('layout.searchPage.results')}`
            : `${t('layout.searchPage.empty')}`;

        return (
            <>
                <h2
                    className="help-page__headline"
                    data-cy="search-results-header"
                >
                    {header}
                </h2>
                <p>{headerParagraph}</p>
            </>
        );
    };

    const getContent = () => {
        if (isLoading) {
            return null;
        }

        const statisticsImg = getStatisticsImg(statisticsUrl);

        if (resultsToDisplay?.length === 0) {
            return statisticsImg;
        }

        return (
            <div data-cy="search-results-wrapper">
                {statisticsImg}
                <ListLinks type="large" links={resultsToDisplay} />
            </div>
        );
    };

    const helmet = searchPhrase && (
        <Helmet>
            <title>{getSearchPageTitle(searchPhrase)}</title>
            <link rel="canonical" href={PATH.helpAndSupport} />
        </Helmet>
    );

    return (
        <>
            {helmet}
            <article id="hs-search-page" className="help-page">
                <header className="help-page__header">
                    <div className="help-page__wrapper">
                        <h1 className="help-page__heading">Help & Support</h1>
                        <Search
                            isFetchingFromSearchResult={isFetchingFromSearchResult}
                            triggerAutocompleteHiding={triggerAutocompleteHiding}
                            hasThePhraseBeenUpdated={() => hasThePhraseBeenUpdated}
                            setSearchPhraseForParent={setSearchPhrase}
                            setResultsToDisplay={setResultsToDisplay}
                        />
                    </div>
                </header>

                <div className="help-page__content help-page__content--short constrain">
                    <div className="help-page__main-col help-page__main-col--single">
                        <div className="help-page__list">
                            <div className="help-page__subheader">
                                {getHeader()}
                            </div>
                            {getContent()}
                        </div>
                        {!isLoading && (
                            <BoxWithUmbracoSearch
                                anchor={ID.searchResultPageId}
                                searchPhrase={searchPhrase}
                            />
                        )}
                    </div>
                </div>
            </article>
            <ContactFooter />
        </>
    );
};

export default SearchResult;
